import Head from 'next/head'
import Link from 'next/link'
import { FunctionComponent, useEffect, useState } from 'react'
import { connectStateResults, Hit, InfiniteHitsProvided } from 'react-instantsearch-core'
import { connectCurrentRefinements, connectHierarchicalMenu, connectInfiniteHits, connectRefinementList } from 'react-instantsearch-dom'

import LoadingIcon from '@/components/icons/Loading'
import SearchIcon from '@/components/icons/Search'
import Layout from '@/components/Layout'
import Product from '@/components/Product'
import AntennaBrandImg from '@/public/img/home/brands/Antenna.png'
import ArrowSVG from '@/public/img/home/brands/Arrow.svg'
import BoltsBrandImg from '@/public/img/home/brands/Bolts.png'
import CableBrandImg from '@/public/img/home/brands/Cable.png'
import DataBrandImg from '@/public/img/home/brands/Data.png'
import ElectricalBrandImg from '@/public/img/home/brands/Electrical.png'
import PpeBrandImg from '@/public/img/home/brands/PPE.png'
import RouterBrandImg from '@/public/img/home/brands/Router.png'
import SafteyBrandImg from '@/public/img/home/brands/Safety.png'
import SuppliesBrandImg from '@/public/img/home/brands/Supplies.png'
import ToolsBrandImg from '@/public/img/home/brands/Tools.png'
import VideoBrandImg from '@/public/img/home/brands/Video.png'
import { calculateUrl } from '@/utils/search'

const SearchFacet: FunctionComponent<{ label: string }> = ({ children, label }) => (
	<div className="p-3 space-y-2">
		<div className="flex justify-between items-center">
			<p className="text-lg font-bold">{label}</p>
			<svg className="h-2" viewBox="0 0 14 8" fill="none">
				<path d="M7.681 7.72l6.043-6.09A.951.951 0 0014 .955a.951.951 0 00-.276-.677.937.937 0 00-1.343 0L7.128 5.571c-.079.08-.177.08-.256 0L1.619.277a.937.937 0 00-1.343 0A.951.951 0 000 .954c0 .259.099.498.276.677L6.32 7.72a.96.96 0 001.362 0z" fill="currentColor" />
			</svg>
		</div>

		{children}
	</div>
)

const CurrentRefinements = connectCurrentRefinements(({ items, refine }) => {
	if (items.length === 0) {
		return null
	}

	return (
		<div className="hidden items-center gap-x-4 gap-y-3 flex-wrap lg:flex">
			{items.map(item => (
				item.items ? (
					item.items.map(refinment => (
						<button onClick={() => {
							refine(refinment.value)
						}} key={refinment.label} className="shadow flex items-center justify-center px-2.5 py-1.5 bg-gray-150 rounded-full">
							<p className="text-sm">{refinment.label}</p>
							<svg className="ml-2" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M9.78332 8.78046L6.01638 4.98642L9.78332 1.21948C10.0543 0.948479 10.0543 0.487774 9.78332 0.216771C9.51232 -0.0542314 9.05161 -0.0542314 8.78061 0.216771L4.98657 3.98371L1.21963 0.216771C0.948631 -0.0542314 0.487927 -0.0542314 0.216924 0.216771C-0.0540788 0.487774 -0.0540788 0.948479 0.216924 1.21948L3.98386 4.98642L0.216924 8.78046C-0.0540788 9.05146 -0.0540788 9.51216 0.216924 9.78317C0.487927 10.0542 0.948631 10.0542 1.21963 9.78317L4.98657 6.01623L8.75351 9.78317C9.02451 10.0542 9.48522 10.0542 9.75622 9.78317C10.0272 9.51216 10.0814 9.05146 9.78332 8.78046Z" fill="#363940"/>
							</svg>
						</button>
					))
				) : (
					<button onClick={() => {
						refine(item.value)
					}} key={item.label} className="shadow flex items-center justify-center px-2.5 py-1.5 bg-gray-150 rounded-full">
						<p className="text-sm">{item.currentRefinement}</p>
						<svg className="ml-2" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M9.78332 8.78046L6.01638 4.98642L9.78332 1.21948C10.0543 0.948479 10.0543 0.487774 9.78332 0.216771C9.51232 -0.0542314 9.05161 -0.0542314 8.78061 0.216771L4.98657 3.98371L1.21963 0.216771C0.948631 -0.0542314 0.487927 -0.0542314 0.216924 0.216771C-0.0540788 0.487774 -0.0540788 0.948479 0.216924 1.21948L3.98386 4.98642L0.216924 8.78046C-0.0540788 9.05146 -0.0540788 9.51216 0.216924 9.78317C0.487927 10.0542 0.948631 10.0542 1.21963 9.78317L4.98657 6.01623L8.75351 9.78317C9.02451 10.0542 9.48522 10.0542 9.75622 9.78317C10.0272 9.51216 10.0814 9.05146 9.78332 8.78046Z" fill="#363940"/>
						</svg>
					</button>
				)
			))}
		</div>
	)
})

const NoResultsRefinements = connectCurrentRefinements(({ items, refine }) => {
	if (items.length === 0) {
		return null
	}

	return (
		<>
			<p className="text-center mt-4 xl:text-lg xl:mt-5">Try removing your current filters.</p>

			<div className="hidden items-center justify-center gap-x-4 gap-y-3 flex-wrap mt-3 lg:flex xl:mt-4">
				{items.map(item => (
					item.items ? (
						item.items.map(refinment => (
							<button onClick={() => {
								refine(refinment.value)
							}} key={refinment.label} className="shadow flex items-center justify-center px-2.5 py-1.5 bg-green-200 text-green-700 rounded-full">
								<p className="text-sm">{refinment.label}</p>
								<svg className="ml-2 text-green-700" width="10" height="10" viewBox="0 0 10 10" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
									<path d="M9.78332 8.78046L6.01638 4.98642L9.78332 1.21948C10.0543 0.948479 10.0543 0.487774 9.78332 0.216771C9.51232 -0.0542314 9.05161 -0.0542314 8.78061 0.216771L4.98657 3.98371L1.21963 0.216771C0.948631 -0.0542314 0.487927 -0.0542314 0.216924 0.216771C-0.0540788 0.487774 -0.0540788 0.948479 0.216924 1.21948L3.98386 4.98642L0.216924 8.78046C-0.0540788 9.05146 -0.0540788 9.51216 0.216924 9.78317C0.487927 10.0542 0.948631 10.0542 1.21963 9.78317L4.98657 6.01623L8.75351 9.78317C9.02451 10.0542 9.48522 10.0542 9.75622 9.78317C10.0272 9.51216 10.0814 9.05146 9.78332 8.78046Z" fill="#363940"/>
								</svg>
							</button>
						))
					) : (
						<button onClick={() => {
							refine(item.value)
						}} key={item.label} className="shadow flex items-center justify-center px-2.5 py-1.5 bg-green-200 text-green-700 rounded-full">
							<p className="text-sm">{item.currentRefinement}</p>
							<svg className="ml-2 text-green-700" width="10" height="10" viewBox="0 0 10 10" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
								<path d="M9.78332 8.78046L6.01638 4.98642L9.78332 1.21948C10.0543 0.948479 10.0543 0.487774 9.78332 0.216771C9.51232 -0.0542314 9.05161 -0.0542314 8.78061 0.216771L4.98657 3.98371L1.21963 0.216771C0.948631 -0.0542314 0.487927 -0.0542314 0.216924 0.216771C-0.0540788 0.487774 -0.0540788 0.948479 0.216924 1.21948L3.98386 4.98642L0.216924 8.78046C-0.0540788 9.05146 -0.0540788 9.51216 0.216924 9.78317C0.487927 10.0542 0.948631 10.0542 1.21963 9.78317L4.98657 6.01623L8.75351 9.78317C9.02451 10.0542 9.48522 10.0542 9.75622 9.78317C10.0272 9.51216 10.0814 9.05146 9.78332 8.78046Z" fill="#363940"/>
							</svg>
						</button>
					)
				))}
			</div>
		</>
	)
})

const NoResults = connectStateResults(({ searchState }) => (
	<div className="lg:ml-8 2xl:ml-14">
		<div className="bg-gray-100 shadow rounded-lg pt-9 pb-6 px-6 lg:w-full xl:py-12">
			<p className="text-center text-lg sm:text-2xl sm:mx-auto sm:max-w-md xl:text-3xl xl:max-w-xl">Sorry, we couldn’t find any results for <span className="font-bold">"{searchState.query}"</span></p>

			<div className="">
				<NoResultsRefinements/>
			</div>

			<div className="bg-white rounded-lg p-6 mt-6 sm:max-w-sm sm:mx-auto xl:flex xl:max-w-full xl:items-center xl:justify-between xl:mt-12 2xl:justify-around">
				<div className="flex sm:items-center">
					<div className="w-1 h-4 rounded bg-gradient-to-b from-green-400 to-green-500 flex-none"></div>
					<p className="text-sm ml-2 sm:text-base 2xl:text-lg">Try searching with more general keywords.</p>
				</div>
				<div className="flex sm:items-center mt-6 xl:mt-0">
					<div className="w-1 h-4 rounded bg-gradient-to-b from-green-400 to-green-500 flex-none"></div>
					<p className="text-sm ml-2 sm:text-base 2xl:text-lg">Search with an exact phrase.</p>
				</div>
				<div className="flex sm:items-center mt-6 xl:mt-0">
					<div className="w-1 h-4 rounded bg-gradient-to-b from-green-400 to-green-500 flex-none"></div>
					<p className="text-sm ml-2 sm:text-base 2xl:text-lg">Check for any small typos.</p>
				</div>
			</div>

			<p className="text-sm mt-6 text-center sm:text-base sm:max-w-md sm:mx-auto xl:text-xl xl:max-w-full xl:mt-14">If you have any questions about a product, one of our experts might be able to help you find it: <a className="text-green-400 font-extrabold block sm:inline xl:block" href="tel:18004447223">1-800-444-7223</a></p>
		</div>

		<section className="container mx-auto text-base font-semibold text-center text-gray-800 md:text-lg">
			<p className="font-bold text-lg mt-8 md:mt-12 md:text-xl xl:text-2xl">Shop our categories</p>

			<div className="flex flex-wrap justify-center py-6 gap-x-10 gap-y-12 md:py-10 xl:gap-x-6">
				<Link href={`/search${calculateUrl('', 'Cabling', [])}`}>
					<a>
						<img className="mx-auto rounded-full w-28 h-28 border-4 border-gray-150 md:w-32 md:h-32 2xl:w-40 2xl:h-40" src={CableBrandImg} alt="Cableing" />
						<p className="mt-2">Cabling</p>
					</a>
				</Link>
				<Link href={`/search${calculateUrl('', 'Data', [])}`}>
					<a>
						<img className="mx-auto rounded-full w-28 h-28 border-4 border-gray-150 md:w-32 md:h-32 2xl:w-40 2xl:h-40" src={DataBrandImg} alt="Data" />
						<p className="mt-2">Data</p>
					</a>
				</Link>
				<Link href={`/search${calculateUrl('router', null, [])}`}>
					<a>
						<img className="mx-auto rounded-full w-28 h-28 border-4 border-gray-150 md:w-32 md:h-32 2xl:w-40 2xl:h-40" src={RouterBrandImg} alt="Routers" />
						<p className="mt-2">Routers</p>
					</a>
				</Link>
				<Link href={`/search${calculateUrl('', 'Video', [])}`}>
					<a>
						<img className="mx-auto rounded-full w-28 h-28 border-4 border-gray-150 md:w-32 md:h-32 2xl:w-40 2xl:h-40" src={VideoBrandImg} alt="Video" />
						<p className="mt-2">Video</p>
					</a>
				</Link>
				<Link href={`/search${calculateUrl('antenna', null, [])}`}>
					<a>
						<img className="mx-auto rounded-full w-28 h-28 border-4 border-gray-150 md:w-32 md:h-32 2xl:w-40 2xl:h-40" src={AntennaBrandImg} alt="Antennas" />
						<p className="mt-2">Antennas</p>
					</a>
				</Link>
				<Link href={`/search${calculateUrl('', 'Electrical', [])}`}>
					<a>
						<img className="mx-auto rounded-full w-28 h-28 border-4 border-gray-150 md:w-32 md:h-32 2xl:w-40 2xl:h-40" src={ElectricalBrandImg} alt="Electrical" />
						<p className="mt-2">Electrical</p>
					</a>
				</Link>
				<Link href={`/search${calculateUrl('', 'Tools', [])}`}>
					<a>
						<img className="mx-auto rounded-full w-28 h-28 border-4 border-gray-150 md:w-32 md:h-32 2xl:w-40 2xl:h-40" src={ToolsBrandImg} alt="Tools" />
						<p className="mt-2">Tools</p>
					</a>
				</Link>
				<Link href={`/search${calculateUrl('', 'Fasteners', [])}`}>
					<a>
						<img className="mx-auto rounded-full w-28 h-28 border-4 border-gray-150 md:w-32 md:h-32 2xl:w-40 2xl:h-40" src={BoltsBrandImg} alt="Fasteners" />
						<p className="mt-2">Fasteners</p>
					</a>
				</Link>
				<Link href={`/search${calculateUrl('safety', null, [])}`}>
					<a>
						<img className="mx-auto rounded-full w-28 h-28 border-4 border-gray-150 md:w-32 md:h-32 2xl:w-40 2xl:h-40" src={SafteyBrandImg} alt="Safety" />
						<p className="mt-2">Safety</p>
					</a>
				</Link>
				<Link href={`/search${calculateUrl('', 'PPE Products', [])}`}>
					<a>
						<img className="mx-auto rounded-full w-28 h-28 border-4 border-gray-150 md:w-32 md:h-32 2xl:w-40 2xl:h-40" src={PpeBrandImg} alt="PPE" />
						<p className="mt-2">PPE Products</p>
					</a>
				</Link>
				<Link href={`/search${calculateUrl('', 'Supplies', [])}`}>
					<a>
						<img className="mx-auto rounded-full w-28 h-28 border-4 border-gray-150 md:w-32 md:h-32 2xl:w-40 2xl:h-40" src={SuppliesBrandImg} alt="Supplies" />
						<p className="mt-2">Supplies</p>
					</a>
				</Link>
				<Link href="/search">
					<a className="">
						<img className="mx-auto rounded-full w-28 h-28 border-4 bg-gray-150 border-gray-150 px-8 md:px-10 md:w-32 md:h-32 2xl:w-40 2xl:h-40" src={ArrowSVG} alt="Shop All" />
						<p className="mt-2">Shop All</p>
					</a>
				</Link>
			</div>
		</section>

	</div>
))

const SearchResults = connectInfiniteHits<InfiniteHitsProvided<Hit<SearchProduct>>, SearchProduct>(({
	hits,
	hasMore,
	refineNext,
}) => (
	<>
		{hits.length === 0 ? (
			<NoResults />
		) : (
			<div className="lg:ml-6 lg:space-y-6 xl:ml-8 2xl:ml-14">
				<CurrentRefinements/>

				<div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2 md:gap-x-8 lg:gap-y-8 xl:grid-cols-3 2xl:gap-x-12 2xl:gap-y-12">
					{hits.map(hit => (
						<Product key={hit.objectID} product={{ ...hit, kind: 'search' }} />
					))}

					{hasMore && (
						<div className="col-span-full flex justify-center items-center">
							<button onClick={refineNext} className="rounded-full border-gray-800 border inline-block px-2.5 py-0.5 text-lg font-extrabold text-gray-800">Show more results</button>
						</div>
					)}
				</div>
			</div>
		)}
	</>
))

const RefinementList = connectRefinementList(({ items, refine }) => (
	<div className="space-y-1">
		{items.length === 0 ? (
			<div>
				<p className="text-gray-300 italic text-sm">None available</p>
			</div>
		) : items.map(item => (
			<button key={item.label} onClick={() => {
				refine(item.value)

				if (document.activeElement instanceof HTMLElement) {
					document.activeElement.blur()
				}
			}} className="flex items-center w-full focus:outline-none">
				<div>
					{item.isRefined ? (
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<rect width="15.9969" height="16" rx="4" fill="#8DC63F" />
							<path d="M6.07956 11.287L12.88 3.0909C13.1084 2.81178 13.5144 2.7864 13.7682 3.01478C14.0473 3.24315 14.0727 3.64915 13.8443 3.9029L6.38406 12.911C6.38406 12.911 6.20644 13.1394 5.90194 13.1394C5.59744 13.1394 5.36906 12.911 5.36906 12.911L2.1972 9.51076C1.96882 9.25701 1.91807 8.87639 2.14645 8.62264C2.4002 8.34351 2.83157 8.34351 3.08532 8.59726L5.59744 11.2616C5.59744 11.2616 5.69894 11.3885 5.85119 11.3885C6.00344 11.3885 6.07956 11.287 6.07956 11.287Z" fill="white" stroke="white" />
						</svg>
					) : (
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<rect x="0.5" y="0.5" width="15" height="15" rx="3.5" fill="white" stroke="#E1E3E5" />
						</svg>
					)}
				</div>
				<p className="ml-2 text-sm">{item.label}</p>
			</button>
		))}
	</div>
))

interface HierarchicalMenuItem {
	count: number,
	isRefined: boolean,
	items: Array<HierarchicalMenuItem> | null,
	label: string,
	value: string,
}

const HierarchicalMenuList = ({ items, refine }: {
	items: Array<HierarchicalMenuItem>,
	refine: (item: string) => unknown,
}) => (
	<>
		{items.length === 0 ? (
			<div>
				<p className="text-gray-300 italic text-sm">None available</p>
			</div>
		) : items.map(item => (
			<li key={item.label}>
				<button className="w-full text-left" onClick={() => {
					refine(item.value)

					if (document.activeElement instanceof HTMLElement) {
						document.activeElement.blur()
					}
				}}>
					<span className={item.isRefined ? 'font-extrabold' : ''}>{item.label}</span>
				</button>

				{item.items && item.items.length > 0 && (
					<ul className="pl-5">
						<HierarchicalMenuList items={item.items} refine={refine} />
					</ul>
				)}
			</li>
		))}
	</>
)

const HierarchicalMenu = connectHierarchicalMenu(({ items, refine }: {
	items: Array<HierarchicalMenuItem>,
	refine: (item: string) => unknown,
}) => (
	<ul>
		<HierarchicalMenuList items={items} refine={refine} />
	</ul>
))

const ClearRefinements = connectCurrentRefinements(({ items, refine }) => (
	<button onClick={() => refine(items)} className="text-sm flex items-center py-1.5 px-3 bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full font-bold shadow-md leading-5 lg:bg-transparent lg:bg-none lg:shadow-none lg:text-black lg:p-0" disabled={!items.length}>
		<svg className="h-5 w-5 xl:h-6 xl:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
			<path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
		</svg>
		<p className="ml-1.5 lg:hidden">Clear Filters</p>
	</button>
))

const PageContent = connectStateResults(({ searchResults }) => {
	const [filterActive, setFilterActive] = useState(false)

	useEffect(() => {
		if (process.browser) {
			window.document.body.classList.toggle('overflow-y-hidden', filterActive)
			window.document.body.classList.toggle('lg:overflow-y-auto', filterActive)
		}
	}, [filterActive])

	/**
	 * According to the types, searchResults is always an object, but in practice it
	 * can be null for a time before the first request has loaded any hits. This is
	 * perfect for being able to tell if we should be displaying a loading state.
	 */
	if ((searchResults as typeof searchResults | null) === null) {
		return (
			<div className="py-32 mx-auto flex justify-center items-center">
				<LoadingIcon />
			</div>
		)
	}

	return (
		<div className="relative container mx-auto px-3 py-6 md:px-4 md:py-10 xl:py-12">
			<div className={`${filterActive ? '' : 'hidden'} bg-black fixed opacity-50 z-40 inset-0 lg:hidden`}></div>

			<div className="lg:flex lg:items-start">
				<div className={`${filterActive ? '' : 'hidden'} fixed inset-0 pt-14 z-50 w-full lg:static lg:z-0 lg:pt-0 lg:w-64 lg:shrink-0 lg:block`}>
					<button aria-hidden onClick={() => setFilterActive(false)} className="h-14 w-full absolute top-0 lg:hidden"></button>

					<div className="h-full flex flex-col shadow border border-gray-200 bg-white rounded-t-lg divide-y divide-gray-200">
						<div className="p-3 lg:flex lg:items-center lg:justify-between">
							<p className="text-xl font-bold">Filters</p>
							<div className="hidden lg:block">
								<ClearRefinements/>
							</div>
						</div>

						<div className="flex-1 divide-y divide-gray-200 overflow-y-scroll lg:overflow-y-auto">
							<SearchFacet label="Brands">
								<RefinementList attribute="brand" />
							</SearchFacet>

							<SearchFacet label="Categories">
								<HierarchicalMenu limit={500} attributes={[
									'categories.lvl0',
									'categories.lvl1',
									'categories.lvl2',
								]} />
							</SearchFacet>
						</div>

						<div className="sticky bottom-0 w-full flex justify-around shadow py-6 bg-white lg:hidden">
							<ClearRefinements />
							<button onClick={() => {
								setFilterActive(!filterActive)
								window.scrollTo(0, 0)
							}} className="text-sm flex items-center py-1.5 px-3 ml-4 bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full font-bold shadow-md leading-5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400">
								<SearchIcon className="text-white w-4 md:h-4" />
								<p className="ml-1.5">See Results</p>
							</button>
						</div>
					</div>
				</div>

				<SearchResults />
			</div>

			<div className={`${filterActive ? 'hidden' : ''} sticky w-full bottom-0 right-0 left-0 flex justify-center items-center pt-6 pb-10 lg:hidden`}>
				<button onClick={() => {
					setFilterActive(!filterActive)
				}} className="text-lg flex items-center py-1.5 px-3 bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full shadow-filter leading-5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400">
					<svg className="w-4 h-4" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 10">
						<g fill="#fff">
							<path d="M3.9.982h7.675c.225 0 .4.177.4.403a.396.396 0 01-.4.403H3.9V.982zM2 1.788H.4a.396.396 0 01-.4-.403C0 1.16.175.982.4.982H2v.806zM10 5.416h1.6c.225 0 .4-.177.4-.403a.396.396 0 00-.4-.403h-1.475L10 5.416zM7.95 5.416H.4a.396.396 0 01-.4-.403c0-.227.175-.403.4-.403h7.55v.806zM4 8.212h7.6c.225 0 .4.176.4.403a.396.396 0 01-.4.403H4v-.806zM1.9 9.043H.4A.396.396 0 010 8.64c0-.227.175-.403.4-.403h1.475l.025.806z"/>
							<path d="M3 2.77c-.75 0-1.375-.629-1.375-1.385C1.625.63 2.25 0 3 0s1.375.63 1.375 1.385c0 .756-.625 1.386-1.375 1.386zM3 .757a.621.621 0 00-.625.63c0 .352.275.63.625.63s.625-.278.625-.63A.621.621 0 003 .755zM3 10c-.75 0-1.375-.63-1.375-1.385 0-.756.625-1.386 1.375-1.386s1.375.63 1.375 1.386C4.375 9.37 3.75 10 3 10zm0-2.015a.621.621 0 00-.625.63c0 .352.275.63.625.63s.625-.278.625-.63A.621.621 0 003 7.985zM9 6.398c-.75 0-1.375-.63-1.375-1.385 0-.756.625-1.386 1.375-1.386s1.375.63 1.375 1.386c0 .755-.625 1.385-1.375 1.385zm0-2.015a.621.621 0 00-.625.63c0 .352.275.63.625.63s.625-.278.625-.63A.621.621 0 009 4.383z"/>
						</g>
					</svg>
					<p className="ml-1.5 font-bold">Filter</p>
				</button>
			</div>
		</div>
	)
})

export default function Search(): JSX.Element {
	return (
		<div className="relative container mx-auto px-4">
			<Head>
				<title key="PageContent-title">Search | Pace International</title>
			</Head>

			<PageContent />
		</div>
	)
}

Search.Layout = Layout
